import { useState, useEffect } from "react";
import { Loader2, Mail, CheckCircle2, AlertCircle, MessageSquare, ArrowRightLeft, CreditCard, FileText, Calendar, RefreshCw, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` });

const EVENT_META = {
  booking_created:    { icon: Sparkles,      color: "#9A7B4F" },
  status_changed:     { icon: RefreshCw,     color: "#3b82f6" },
  products_changed:   { icon: ArrowRightLeft,color: "#9A7B4F" },
  email_sent:         { icon: CheckCircle2,  color: "#10b981" },
  email_failed:       { icon: AlertCircle,   color: "#ef4444" },
  payment_received:   { icon: CreditCard,    color: "#10b981" },
  damage_assessment:  { icon: AlertCircle,   color: "#f59e0b" },
  appointment:        { icon: Calendar,      color: "#6b7280" },
  admin_note_added:   { icon: MessageSquare, color: "#6b7280" },
  admin_note_deleted: { icon: MessageSquare, color: "#9ca3af" },
};

function relTime(iso) {
  if (!iso) return "—";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (isNaN(ms)) return "—";
    if (ms < 60_000) return "just now";
    const m = Math.floor(ms / 60000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch { return "—"; }
}

function fullTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export function BookingActivityTimeline({ bookingId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchActivity = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/bookings/${bookingId}/activity`, { headers: auth() });
      setEvents(r.data.events || []);
    } catch {
      toast.error("Failed to load activity");
    }
    setLoading(false);
  };

  useEffect(() => { fetchActivity(); /* eslint-disable-next-line */ }, [bookingId]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#9A7B4F]" /></div>;
  if (events.length === 0) {
    return <div className="text-center py-10 text-[12px] text-[#676664]/50">No activity recorded for this booking yet.</div>;
  }

  return (
    <div className="relative" data-testid="activity-timeline">
      <button onClick={fetchActivity} className="absolute right-0 top-0 text-[10px] text-[#9A7B4F] hover:underline uppercase tracking-wider" data-testid="activity-refresh">refresh</button>
      <div className="border-l-2 border-[#282828]/8 ml-4 space-y-3 pt-1">
        {events.map((e) => {
          const meta = EVENT_META[e.type] || EVENT_META.status_changed;
          const Icon = meta.icon;
          return (
            <div key={e.id} className="relative pl-6" data-testid={`activity-event-${e.type}`}>
              <div className="absolute -left-[11px] top-0.5 w-5 h-5 rounded-full bg-white border-2 flex items-center justify-center" style={{ borderColor: meta.color }}>
                <Icon className="w-2.5 h-2.5" style={{ color: meta.color }} />
              </div>
              <div className="bg-white border border-[#282828]/8 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[12px] text-[#282828] font-medium">{e.label}</span>
                  <span className="text-[10px] text-[#676664]/50 whitespace-nowrap" title={fullTime(e.timestamp)}>{relTime(e.timestamp)}</span>
                </div>
                <div className="text-[10px] text-[#676664]/60 mt-0.5">by {e.actor}</div>
                {e.details && Object.keys(e.details).length > 0 && (
                  <div className="text-[10.5px] text-[#676664] mt-1.5 space-y-0.5">
                    {renderDetails(e.type, e.details)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function renderDetails(type, d) {
  if (type === "products_changed") {
    const adj = d.payment_adjustment || {};
    return (
      <>
        {d.before?.outfit_name && d.after?.outfit_name && (
          <div><span className="text-[#676664]/50">Outfit:</span> {d.before.outfit_name} → <strong>{d.after.outfit_name}</strong></div>
        )}
        {(d.before?.total_amount !== undefined && d.after?.total_amount !== undefined) && (
          <div><span className="text-[#676664]/50">Total:</span> ${d.before.total_amount?.toFixed(2)} → <strong>${d.after.total_amount?.toFixed(2)}</strong></div>
        )}
        {adj.action && (
          <div><span className="text-[#676664]/50">Action:</span> <span className="font-mono text-[10px]">{adj.action}</span> {adj.note ? `· "${adj.note}"` : ""}</div>
        )}
      </>
    );
  }
  if (type === "email_sent" || type === "email_failed") {
    return (
      <>
        {d.subject && <div className="truncate" title={d.subject}>📧 {d.subject}</div>}
        {d.to_email && <div className="text-[#676664]/50">→ {d.to_email}</div>}
        {d.error_message && <div className="text-red-600 mt-0.5">{d.error_message}</div>}
      </>
    );
  }
  if (type === "payment_received") {
    return <div>${d.amount?.toFixed?.(2) ?? d.amount} {d.currency || "AUD"} · {d.status}</div>;
  }
  if (type === "admin_note_added") {
    return <div className="italic">"{d.note?.slice(0, 200)}{d.note?.length > 200 ? "…" : ""}"</div>;
  }
  if (type === "booking_created") {
    return (
      <>
        {d.source && <div className="text-[#676664]/50">source: <span className="font-mono">{d.source}</span></div>}
        {d.outfit_name && <div>{d.outfit_name}</div>}
      </>
    );
  }
  return null;
}
